/**
 * A photo of a signature on paper → its ink alone on a clear background, cropped to it. The paper is found by a
 * morphological closing (a running max then min, wider than a pen stroke), which keeps a shadow's edge where it is, so
 * dividing by it takes shadows and uneven light out. Strokes near each other make words; the word nearest the middle,
 * weighed by its ink, is the signature (a name cut by the photo's edge is left out). Pure, so it runs on a canvas's
 * pixels in the browser and in tests alike.
 */

export interface Pixels {
  /** RGBA, 4 bytes a pixel (a canvas's ImageData) */
  data: Uint8ClampedArray
  width: number
  height: number
}

export interface CleanedSignature extends Pixels {
  /** Blue ink stays blue, any other dark ink is drawn near black */
  ink: 'blue' | 'black'
}

export interface CleanOptions {
  /** 0 keeps only strong ink, 1 faint lines too; 0.5 suits most photos */
  sensitivity?: number
}

const BLUE_INK = [22, 46, 140] as const
const BLACK_INK = [20, 22, 28] as const

/** A running max (`grow`) or min over a window of 2r + 1 along every row, then every column (a monotonic queue: O(n)) */
function slide(src: Float32Array, width: number, height: number, r: number, grow: boolean): Float32Array {
  const sign = grow ? 1 : -1
  const pass = (from: Float32Array, to: Float32Array, rows: boolean) => {
    const length = rows ? width : height
    const lines = rows ? height : width
    const step = rows ? 1 : width
    const queue = new Int32Array(length)
    for (let line = 0; line < lines; line++) {
      const start = rows ? line * width : line
      let head = 0
      let tail = 0
      let next = 0
      for (let i = 0; i < length; i++) {
        for (const last = Math.min(length - 1, i + r); next <= last; next++) {
          const value = sign * (from[start + next * step] as number)
          while (tail > head && value >= sign * (from[start + (queue[tail - 1] as number) * step] as number)) tail--
          queue[tail++] = next
        }
        while ((queue[head] as number) < i - r) head++
        to[start + i * step] = from[start + (queue[head] as number) * step] as number
      }
    }
  }
  const across = new Float32Array(src.length)
  const out = new Float32Array(src.length)
  pass(src, across, true)
  pass(across, out, false)
  return out
}

/** The value `share` of `values` (0–255) are at or below, by their histogram */
function percentile(values: Float32Array, share: number): number {
  const counts = new Uint32Array(256)
  for (const value of values) {
    const at = Math.min(255, Math.max(0, Math.round(value)))
    counts[at] = (counts[at] as number) + 1
  }
  let seen = 0
  for (let v = 0; v < 256; v++) {
    seen += counts[v] as number
    if (seen >= values.length * share) return v
  }
  return 255
}

/** Connected regions of `mask` (4 neighbours): each pixel's region, and each region's size and box */
function regions(mask: Uint8Array, width: number, height: number) {
  const label = new Int32Array(mask.length).fill(-1)
  const sizes: number[] = []
  const boxes: [number, number, number, number][] = []
  const stack = new Int32Array(mask.length)
  for (let seed = 0; seed < mask.length; seed++) {
    if (!mask[seed] || (label[seed] as number) >= 0) continue
    const id = sizes.length
    let top = 0
    let size = 0
    let [x0, y0, x1, y1] = [width, height, 0, 0]
    stack[top++] = seed
    label[seed] = id
    while (top) {
      const p = stack[--top] as number
      const x = p % width
      const y = (p - x) / width
      size++
      x0 = Math.min(x0, x)
      x1 = Math.max(x1, x)
      y0 = Math.min(y0, y)
      y1 = Math.max(y1, y)
      for (let k = 0; k < 4; k++) {
        const q = k === 0 ? (x > 0 ? p - 1 : -1) : k === 1 ? (x < width - 1 ? p + 1 : -1) : k === 2 ? p - width : p + width
        if (q < 0 || q >= mask.length || !mask[q] || (label[q] as number) >= 0) continue
        label[q] = id
        stack[top++] = q
      }
    }
    sizes.push(size)
    boxes.push([x0, y0, x1, y1])
  }
  return { label, sizes, boxes }
}

/** The signature in `photo`, or null when there is no ink on paper in it */
export function cleanSignature(photo: Pixels, options: CleanOptions = {}): CleanedSignature | null {
  const { data, width, height } = photo
  const n = width * height
  const dim = Math.max(width, height)
  // The darkest channel: blue or black ink stands out of white or shaded paper alike
  const grey = new Float32Array(n)
  for (let i = 0; i < n; i++) grey[i] = Math.min(data[i * 4] as number, data[i * 4 + 1] as number, data[i * 4 + 2] as number)
  const r = Math.max(4, Math.round(dim * 0.012))
  const paper = slide(slide(grey, width, height, r, true), width, height, r, false)
  // What is far darker than the brightest paper is no paper (a desk, a dark border): nothing there is ink
  const lit = percentile(paper, 0.95)
  const hi = 0.7 + 0.2 * Math.min(1, Math.max(0, options.sensitivity ?? 0.5))
  const lo = hi - 0.3
  const alpha = new Float32Array(n)
  for (let i = 0; i < n; i++) {
    const own = paper[i] as number
    if (own < lit * 0.45) continue
    const ratio = (grey[i] as number) / Math.max(own, 8)
    alpha[i] = Math.min(1, Math.max(0, (hi - ratio) / (hi - lo)))
  }

  // Specks out; strokes near each other joined into words
  const ink = new Uint8Array(n)
  for (let i = 0; i < n; i++) ink[i] = (alpha[i] as number) > 0.3 ? 1 : 0
  const strokes = regions(ink, width, height)
  const speck = Math.max(6, Math.round(n * 0.00002))
  for (let i = 0; i < n; i++) if (ink[i] && (strokes.sizes[strokes.label[i] as number] as number) < speck) ink[i] = 0
  const near = slide(Float32Array.from(ink), width, height, Math.round(dim * 0.025), true)
  const words = regions(
    Uint8Array.from(near, (v) => (v > 0 ? 1 : 0)),
    width,
    height,
  )

  // The word nearest the middle by its ink; one the photo's edge cuts counts for less
  const inkOf = new Float64Array(words.sizes.length)
  for (let i = 0; i < n; i++) {
    const word = words.label[i] as number
    if (ink[i]) inkOf[word] = (inkOf[word] as number) + 1
  }
  let best = -1
  let bestScore = 0
  words.boxes.forEach(([x0, y0, x1, y1], id) => {
    const off = Math.hypot((x0 + x1) / 2 / width - 0.5, (y0 + y1) / 2 / height - 0.5)
    const cut = x0 <= 1 || y0 <= 1 || x1 >= width - 2 || y1 >= height - 2 ? 0.35 : 1
    const score = ((inkOf[id] as number) * cut) / (1 + 6 * off)
    if (score > bestScore) {
      bestScore = score
      best = id
    }
  })
  if (best < 0) return null

  let [x0, y0, x1, y1] = [width, height, 0, 0]
  let [red, green, blue, counted] = [0, 0, 0, 0]
  for (let i = 0; i < n; i++) {
    if (words.label[i] !== best || !ink[i]) continue
    const x = i % width
    const y = (i - x) / width
    x0 = Math.min(x0, x)
    x1 = Math.max(x1, x)
    y0 = Math.min(y0, y)
    y1 = Math.max(y1, y)
    if ((alpha[i] as number) > 0.9) {
      red += data[i * 4] as number
      green += data[i * 4 + 1] as number
      blue += data[i * 4 + 2] as number
      counted++
    }
  }
  // The ink's own colour, made deep and even
  const isBlue = counted > 0 && (blue - Math.max(red, green)) / counted > 18
  const colour = isBlue ? BLUE_INK : BLACK_INK
  const pad = Math.round(dim * 0.02)
  const left = Math.max(0, x0 - pad)
  const top = Math.max(0, y0 - pad)
  const outWidth = Math.min(width, x1 + pad + 1) - left
  const outHeight = Math.min(height, y1 + pad + 1) - top
  const out = new Uint8ClampedArray(outWidth * outHeight * 4)
  for (let y = 0; y < outHeight; y++) {
    for (let x = 0; x < outWidth; x++) {
      const i = (y + top) * width + x + left
      const o = (y * outWidth + x) * 4
      out[o] = colour[0]
      out[o + 1] = colour[1]
      out[o + 2] = colour[2]
      out[o + 3] = words.label[i] === best ? Math.round((alpha[i] as number) ** 0.8 * 255) : 0
    }
  }
  return { data: out, width: outWidth, height: outHeight, ink: isBlue ? 'blue' : 'black' }
}

/** The box of what is drawn in `pixels` (alpha above a trace), padded; null when nothing is */
export function inkBox(pixels: Pixels, pad = 0): { x: number; y: number; width: number; height: number } | null {
  const { data, width, height } = pixels
  let [x0, y0, x1, y1] = [width, height, -1, -1]
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      if ((data[(y * width + x) * 4 + 3] as number) <= 8) continue
      x0 = Math.min(x0, x)
      x1 = Math.max(x1, x)
      y0 = Math.min(y0, y)
      y1 = Math.max(y1, y)
    }
  }
  if (x1 < 0) return null
  const x = Math.max(0, x0 - pad)
  const y = Math.max(0, y0 - pad)
  return { x, y, width: Math.min(width, x1 + pad + 1) - x, height: Math.min(height, y1 + pad + 1) - y }
}
